import { useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StorefrontApiError } from '../../../../lib/api/storefront-client.js';
import { clearGuestCartToken, getStoredGuestCartToken, storeGuestCartToken } from '../../../../lib/guest-cart-token.js';
import {
  fetchCart,
  addCartItem,
  updateCartItem,
  removeCartItem,
  clearCart,
  createGuestCartSession,
  mergeGuestCart,
} from '../api/cart-api.js';
import type { AddCartItemInput, UpdateCartItemInput } from '../types/cart.js';

const CART_KEY = ['storefront', 'cart'] as const;

async function requireToken(getToken: () => Promise<string | null>): Promise<string> {
  const token = await getToken();
  if (!token) {
    throw new StorefrontApiError('AUTHENTICATION_ERROR', 'Your session has expired. Please sign in again.', 401);
  }
  return token;
}

/**
 * Returns the current guest cart token, creating one via `POST
 * /cart/guest-session` if this browser tab doesn't have one yet. Lazy by
 * design -- called only from a mutation (add/update/remove/clear), never
 * from the read query, so a signed-out visitor who never touches the cart
 * never causes a guest cart row to be created at all.
 */
async function requireGuestToken(): Promise<string> {
  const existing = getStoredGuestCartToken();
  if (existing) return existing;
  const { guestToken } = await createGuestCartSession();
  storeGuestCartToken(guestToken);
  return guestToken;
}

function nonRetryableStatus(error: unknown): boolean {
  return error instanceof StorefrontApiError && [401, 403, 404, 422].includes(error.status);
}

/**
 * The single cart query every consumer shares -- the header's cart badge,
 * the cart drawer, the cart page, and the checkout summary all read this
 * same query key, so a mutation from any one of them (add/update/remove)
 * invalidates and refreshes the others automatically via TanStack Query's
 * cache, never independently-drifting copies of "the cart".
 *
 * Works for both signed-in and signed-out visitors: signed-in fetches with
 * the Clerk token as before (unchanged); signed-out only fetches if a
 * guest cart token already exists in this tab's `sessionStorage` -- a
 * signed-out visitor who hasn't added anything yet has no guest cart to
 * fetch, and this deliberately never creates one just to read it (see
 * `requireGuestToken`'s doc comment -- creation is mutation-triggered
 * only).
 */
export function useCart() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const guestToken = isSignedIn ? null : getStoredGuestCartToken();

  return useQuery({
    queryKey: CART_KEY,
    queryFn: async ({ signal }) => {
      if (isSignedIn) {
        return fetchCart({ token: await requireToken(getToken), signal });
      }
      return fetchCart({ guestToken, signal });
    },
    enabled: isLoaded && (isSignedIn === true || Boolean(guestToken)),
    staleTime: 10_000,
    retry: (count, error) => !nonRetryableStatus(error) && count < 2,
  });
}

/** Cart item count for badges -- derives from the same shared query, never a second network call. */
export function useCartItemCount(): number {
  const { data } = useCart();
  return data?.itemCount ?? 0;
}

export function useCartMutations() {
  const { isSignedIn, getToken } = useAuth();
  const queryClient = useQueryClient();
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: CART_KEY });

  async function identityOptions(): Promise<{ token: string } | { guestToken: string }> {
    if (isSignedIn) return { token: await requireToken(getToken) };
    return { guestToken: await requireGuestToken() };
  }

  const add = useMutation({
    mutationFn: async (input: AddCartItemInput) => addCartItem(input, await identityOptions()),
    onSuccess: (view) => queryClient.setQueryData(CART_KEY, view),
  });

  const update = useMutation({
    mutationFn: async ({ itemId, input }: { itemId: string; input: UpdateCartItemInput }) =>
      updateCartItem(itemId, input, await identityOptions()),
    onSuccess: (view) => queryClient.setQueryData(CART_KEY, view),
  });

  const remove = useMutation({
    mutationFn: async (itemId: string) => removeCartItem(itemId, await identityOptions()),
    onSuccess: (view) => queryClient.setQueryData(CART_KEY, view),
  });

  const clear = useMutation({
    mutationFn: async () => clearCart(await identityOptions()),
    onSuccess: invalidate,
  });

  return { add, update, remove, clear };
}

/**
 * Merges a signed-out visitor's guest cart into their account the moment
 * they sign in -- mounted once near the app root (`AppShell`) so it runs
 * regardless of which page the sign-in happened on. Fires exactly once
 * per signed-out-to-signed-in transition (the `hasAttempted` ref, not a
 * dependency on `isSignedIn` alone, guards against re-firing on every
 * re-render while signed in). The guest token is cleared from
 * `sessionStorage` unconditionally afterward, success or failure -- a
 * merge is not retried; a failed merge just leaves the guest cart
 * abandoned (never re-attempted, since the token clears either way and
 * the backend has no route to merge an already-converted or arbitrary
 * cart without one).
 */
export function useMergeGuestCartOnSignIn(): void {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const queryClient = useQueryClient();
  const hasAttempted = useRef(false);

  useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn) {
      hasAttempted.current = false;
      return;
    }

    if (hasAttempted.current) return;
    const guestToken = getStoredGuestCartToken();
    if (!guestToken) return;

    hasAttempted.current = true;
    void (async () => {
      try {
        const token = await requireToken(getToken);
        await mergeGuestCart(guestToken, { token });
        void queryClient.invalidateQueries({ queryKey: CART_KEY });
      } catch {
        // Never retry-loop a merge -- a failed merge just leaves the
        // (now-orphaned) guest cart un-merged. The token is cleared below
        // regardless, so nothing re-attempts it.
      } finally {
        clearGuestCartToken();
      }
    })();
  }, [isLoaded, isSignedIn, getToken, queryClient]);
}
